/**
 * RAG Service — Retrieval-Augmented Generation para LIZ
 * Busca documentos verificados da base de conhecimento
 * e prepara o contexto para respostas verificadas.
 *
 * Regra de Ouro: Nunca responda sem fonte verificada
 */

import { lerToken } from '../../services/sessao';

interface VerifiedDocument {
  id: string;
  title: string;
  category: 'law' | 'education' | 'financial' | 'tech' | 'marketing' | 'sistema';
  sourceTitle: string;
  sourceUrl: string;
  citationFormat: 'ABNT' | 'APA' | 'ISO690';
  isOfficial: boolean;
  verifiedBy: string;
  verifiedAt: string;
  version: string;
  lastUpdated: string;
  keywords: string[];
  summary: string;
  excerpt: string;
  sections?: Array<{ title: string; content: string }>;
  applicableContext?: string[];
  status: 'approved' | 'pending' | 'rejected';
}

interface SearchResult {
  document: VerifiedDocument;
  relevanceScore: number; // 0-1
  matchedKeywords: string[];
  applicableSection?: string;
}

interface ContextForResponse {
  query: string;
  relevantDocuments: SearchResult[];
  hasSourced: boolean;
  formattedCitations: string[];
  confidence: 'high' | 'medium' | 'low';
  requiresWebSearch: boolean;
  disclaimer?: string;
}

class RAGService {
  private documents: VerifiedDocument[] = [];
  private isInitialized = false;

  /**
   * Inicializar o serviço carregando documentos
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Em produção, isso viria de uma API
      // Por enquanto, usamos importação estática
      const docsData = await import('./documents.json');
      this.documents = (docsData.default.documents || []) as any[];
      this.isInitialized = true;
      console.log(`✅ RAG Service inicializado com ${this.documents.length} documentos`);
    } catch (error) {
      console.error('❌ Erro ao inicializar RAG Service:', error);
      this.documents = [];
    }
  }

  /**
   * Buscar documentos relevantes para uma pergunta
   * @param query Pergunta do usuário
   * @param topK Número de resultados (padrão: 3)
   */
  async searchDocuments(query: string, topK = 3): Promise<SearchResult[]> {
    await this.initialize();

    const lowercaseQuery = query.toLowerCase();
    const queryTokens = lowercaseQuery.split(/\s+/).filter(t => t.length > 2);

    // Calcular relevância de cada documento
    const scored = this.documents
      .filter(doc => doc.status === 'approved')
      .map(doc => {
        let relevanceScore = 0;

        // Score por keywords
        const matchedKeywords = doc.keywords.filter(kw =>
          queryTokens.some(token => kw.includes(token) || token.includes(kw))
        );
        relevanceScore += matchedKeywords.length * 0.3;

        // Score por título
        if (doc.title.toLowerCase().includes(lowercaseQuery)) {
          relevanceScore += 0.4;
        }

        // Score por summary
        if (doc.summary.toLowerCase().includes(lowercaseQuery)) {
          relevanceScore += 0.2;
        }

        // Score por excerpt
        if (doc.excerpt.toLowerCase().includes(lowercaseQuery)) {
          relevanceScore += 0.15;
        }

        // Score por seções
        let applicableSection: string | undefined;
        if (doc.sections) {
          for (const section of doc.sections) {
            if (section.content.toLowerCase().includes(lowercaseQuery)) {
              relevanceScore += 0.15;
              applicableSection = section.title;
              break;
            }
          }
        }

        // Score por contexto aplicável
        if (doc.applicableContext?.some(ctx => lowercaseQuery.includes(ctx))) {
          relevanceScore += 0.2;
        }

        return {
          document: doc,
          relevanceScore: Math.min(relevanceScore, 1),
          matchedKeywords,
          applicableSection,
        };
      })
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .filter(result => result.relevanceScore > 0)
      .slice(0, topK);

    return scored;
  }

  /**
   * Preparar contexto para resposta da LIZ
   */
  async prepareContext(query: string): Promise<ContextForResponse> {
    const relevantDocuments = await this.searchDocuments(query);
    const hasSourced = relevantDocuments.length > 0;
    const formattedCitations = relevantDocuments.map(result =>
      this.formatCitation(result.document, result.document.citationFormat)
    );

    const confidence =
      relevantDocuments.length >= 3 ? 'high' :
      relevantDocuments.length >= 1 ? 'medium' : 'low';

    // Se não encontrou documentos relevantes, pode ser necessário web search
    const requiresWebSearch = !hasSourced;

    let disclaimer: string | undefined;
    if (confidence === 'low') {
      disclaimer = '⚠️ Resposta com confiança baixa — recomenda-se consultar fontes oficiais';
    }

    return {
      query,
      relevantDocuments,
      hasSourced,
      formattedCitations,
      confidence,
      requiresWebSearch,
      disclaimer,
    };
  }

  /**
   * Formatar citação em ABNT, APA ou ISO690
   */
  private formatCitation(doc: VerifiedDocument, format: 'ABNT' | 'APA' | 'ISO690'): string {
    const verifiedDate = new Date(doc.verifiedAt).toLocaleDateString('pt-BR');

    switch (format) {
      case 'ABNT':
        return `[ABNT] ${doc.sourceTitle}. Disponível em: ${doc.sourceUrl}. Acesso em: ${verifiedDate}`;

      case 'APA':
        return `[APA] ${doc.sourceTitle}. Retrieved from ${doc.sourceUrl} (${verifiedDate})`;

      case 'ISO690':
        return `[ISO 690] ${doc.sourceTitle}. Available at: ${doc.sourceUrl}. Accessed: ${verifiedDate}`;

      default:
        return `${doc.sourceTitle} — ${doc.sourceUrl}`;
    }
  }

  /**
   * Gerar prompt para LIZ com contexto verificado
   */
  async generateSystemPrompt(context: ContextForResponse): Promise<string> {
    const basePrompt = `Você é a Liz, assistente jurídica-estratégica do Gestão Creches.

REGRA ABSOLUTA — INEGOCIÁVEL:
✋ NUNCA invente respostas sem fonte
✋ SEMPRE cite o documento ou diga "Não tenho informação verificada"
✋ Se não encontrar documento relevante, pergunte mais ou suira web search
✋ SEMPRE indique data de verificação da fonte
✋ Use citações estruturadas (ABNT/APA/ISO690)

CONTEXTO DISPONÍVEL (${context.relevantDocuments.length} documento(s)):`;

    let contextSection = '';
    for (const result of context.relevantDocuments) {
      contextSection += `\n\n### ${result.document.title}
**Categoria:** ${result.document.category}
**Confiança:** ${(result.relevanceScore * 100).toFixed(0)}%
**Resumo:** ${result.document.summary}
${result.applicableSection ? `**Seção Relevante:** ${result.applicableSection}\n` : ''}
**Trecho:** "${result.document.excerpt}"
**Citação:** ${this.formatCitation(result.document, result.document.citationFormat)}`;
    }

    const confidenceNote = `\n\n⚡ Confiança da Resposta: ${context.confidence.toUpperCase()}`;
    const disclaimerNote = context.disclaimer ? `\n\n${context.disclaimer}` : '';

    return `${basePrompt}${contextSection}${confidenceNote}${disclaimerNote}`;
  }

  /**
   * Validar se uma resposta precisa de fonte verificada
   * (usado para validar respostas geradas)
   */
  validateResponseHasSource(response: string): { isValid: boolean; issue?: string } {
    // Verificar se tem citação estruturada
    const hasCitation = /\[(ABNT|APA|ISO|fonte)\]/i.test(response);
    const hasUrl = /https?:\/\/[^\s)]+/i.test(response);
    const hasDateRef = /\d{1,2}\/\d{1,2}\/\d{4}|20\d{2}/i.test(response);

    if (!hasCitation && !hasUrl) {
      return {
        isValid: false,
        issue: 'Resposta sem citação de fonte verificada',
      };
    }

    return { isValid: true };
  }

  /**
   * Listar todos os documentos disponíveis (para admin)
   */
  async listDocuments(category?: string): Promise<VerifiedDocument[]> {
    try {
      const token = lerToken('saas') || '';
      const url = '/api/ia/knowledge-base/documents' + (category ? `?category=${category}` : '');
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      return (data.documentos || []) as VerifiedDocument[];
    } catch (err) {
      console.error('Erro ao listar documentos do backend:', err);
      return [];
    }
  }

  /**
   * Adicionar novo documento via backend
   */
  async addDocument(doc: Partial<VerifiedDocument>): Promise<boolean> {
    try {
      const token = lerToken('saas') || '';
      const resp = await fetch('/api/ia/knowledge-base/documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          title: doc.title,
          category: doc.category,
          sourceTitle: doc.sourceTitle,
          sourceUrl: doc.sourceUrl,
          citationFormat: doc.citationFormat || 'ABNT',
          summary: doc.summary,
          excerpt: doc.excerpt || doc.summary,
          keywords: doc.keywords,
          applicableContext: doc.applicableContext,
          verifiedBy: doc.verifiedBy || 'admin',
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        console.error('❌ Backend rejeitou documento:', text);
        return false;
      }
      console.log(`✅ Documento adicionado: ${doc.title}`);
      return true;
    } catch (err) {
      console.error('Erro ao adicionar documento:', err);
      return false;
    }
  }

  /**
   * Gerar relatório de documentos (do backend)
   */
  async generateReport(): Promise<{
    totalDocuments: number;
    byCategory: Record<string, number>;
    officialSources: number;
    lastUpdated: string;
  }> {
    try {
      const token = lerToken('saas') || '';
      const resp = await fetch('/api/ia/chat/statistics', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      return {
        totalDocuments: data.totalDocuments || 0,
        byCategory: data.byCategory || {},
        officialSources: data.officialSources || 0,
        lastUpdated: data.lastUpdated || new Date().toISOString(),
      };
    } catch (err) {
      console.error('Erro ao buscar estatísticas:', err);
      return { totalDocuments: 0, byCategory: {}, officialSources: 0, lastUpdated: new Date().toISOString() };
    }
  }
}

// Instância singleton
export const ragService = new RAGService();
